/**
 * useResizeMetrics — connects the resize instrumentation to real events.
 *
 * `resizeMetrics` shipped as pure logic with nothing calling it, which made
 * the "did the bigger targets work?" question unanswerable — and that gap is
 * why two rounds of handle fixes were judged by hand and one of them was
 * wrong. This is the wiring.
 *
 * Three feeds:
 *
 *   ACQUIRE — a capture-phase `pointerdown`. Capture matters: the handle's own
 *   listener stops propagation, so a bubble-phase listener would never see the
 *   press it is trying to measure.
 *
 *   RESIZE — which path produced a size change (handle drag vs preset vs
 *   numeric vs keyboard), reported by the caller via `reportResizeVerb`.
 *
 *   REGRET — an undo landing within the window after a resize.
 *
 * Nothing here reads page content, text, or site-identifying ids: only handle
 * ids, pixel distances, elapsed ms, and which input device. The sink defaults
 * to a no-op, so instrumentation is never a side effect nobody asked for.
 */
import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useEditorContextOptional } from "./EditorContext.js";
import {
  classifyAcquisition,
  createRegretTracker,
  currentPointerKind,
  noopSink,
  type HandleId,
  type MetricsSink,
  type ResizeVerb,
} from "./resizeMetrics.js";

const HANDLE_IDS = new Set<string>([
  "n",
  "s",
  "e",
  "w",
  "ne",
  "nw",
  "se",
  "sw",
]);

interface MetricsApi {
  /** Report that a size changed, and by which path. */
  readonly reportResizeVerb: (verb: ResizeVerb) => void;
  /** Report an undo, so it can be matched against a recent resize. */
  readonly reportUndo: () => void;
}

const NOOP_API: MetricsApi = {
  reportResizeVerb: () => {},
  reportUndo: () => {},
};

const MetricsContext = createContext<MetricsApi>(NOOP_API);

export function useResizeMetricsApi(): MetricsApi {
  return useContext(MetricsContext);
}

export function ResizeMetricsProvider({
  sink = noopSink,
  children,
}: {
  /** Where events go. Defaults to discarding them. */
  readonly sink?: MetricsSink;
  readonly children: React.ReactNode;
}): React.ReactElement {
  const sinkRef = useRef(sink);
  sinkRef.current = sink;
  const regret = useMemo(() => createRegretTracker(), []);
  // The tracker answers "was this within the window?" but not "how long?" —
  // and emitting a hardcoded elapsed would put a lie in the data, which is
  // worse than not measuring.
  const lastResizeAt = useRef<number | null>(null);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      const handleEl = target?.closest?.(
        "[data-resize-handle]",
      ) as HTMLElement | null;
      const handle = handleEl?.dataset["resizeHandle"];
      if (!handle || !HANDLE_IDS.has(handle)) return;
      // The item is the handle's positioned ancestor; without its rect there
      // is no corner to measure against, so skip rather than guess.
      const itemEl = handleEl?.closest(
        "[data-grid-item]",
      ) as HTMLElement | null;
      if (!itemEl) return;
      const r = itemEl.getBoundingClientRect();
      sinkRef.current({
        type: "acquire",
        pointerKind: currentPointerKind(),
        ...classifyAcquisition(
          handle as HandleId,
          { x: e.clientX, y: e.clientY },
          { left: r.left, top: r.top, width: r.width, height: r.height },
        ),
      });
    };
    // Capture phase: the handle stops propagation, so bubble never fires.
    window.addEventListener("pointerdown", onDown, { capture: true });
    return () =>
      window.removeEventListener("pointerdown", onDown, { capture: true });
  }, []);

  const api = useMemo<MetricsApi>(
    () => ({
      reportResizeVerb(verb) {
        const now = Date.now();
        lastResizeAt.current = now;
        regret.resized(now);
        sinkRef.current({
          type: "resize",
          verb,
          pointerKind: currentPointerKind(),
        });
      },
      reportUndo() {
        const at = Date.now();
        const since = lastResizeAt.current;
        if (regret.undone(at))
          sinkRef.current({
            type: "regret",
            sinceResizeMs: since === null ? 0 : at - since,
          });
        lastResizeAt.current = null;
      },
    }),
    [regret],
  );

  // The undo half of the regret signal. Subscribing to the UndoManager rather
  // than to Cmd+Z means menu/programmatic undo counts too, and there is no
  // second copy of the keyboard matcher in useEditorDoc to drift from.
  // `stack-item-popped` fires for redo as well, hence the type filter.
  const undoManager = useEditorContextOptional()?.undoManager ?? null;
  useEffect(() => {
    if (!undoManager) return;
    const onPopped = (ev: { type: "undo" | "redo" }) => {
      if (ev.type === "undo") apiRef.current.reportUndo();
    };
    undoManager.on("stack-item-popped", onPopped);
    return () => undoManager.off("stack-item-popped", onPopped);
  }, [undoManager]);

  const apiRef = useRef(api);
  apiRef.current = api;

  return (
    <MetricsContext.Provider value={api}>{children}</MetricsContext.Provider>
  );
}
